import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Divider,
  Paper,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

// Import separated components
import QRCodeSection from "../components/claim/QRCodeSection";
import LoginSection from "../components/claim/LoginSection";
import DiveFormSection from "../components/claim/DiveFormSection";

const ClaimDivePage = () => {
  const { token } = useParams();
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validToken, setValidToken] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [diveData, setDiveData] = useState(null);
  const [claimCompleted, setClaimCompleted] = useState(false);

  useEffect(() => {
    // Store the token in session storage for when user logs in
    if (!currentUser) {
      sessionStorage.setItem("pendingClaimToken", token);
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        setLoading(true);
        const firebaseToken = await currentUser.getIdToken();

        const response = await axios.get(
          `/api/sharing/claim/${token}`,
          {
            headers: {
              Authorization: `Bearer ${firebaseToken}`,
            },
          }
        );

        console.log("RESPONSE:", response.data);

        if (response.data.success) {
          setValidToken(true);
          setTokenData(response.data);

          // Check if this is already claimed
          if (response.data.alreadyClaimed) {
            setClaimCompleted(true);
            setDiveData(response.data.dive);
          } 
            let initialDiveData = response.data.dive;
            try {
                const divesiteResponse = await axios.get(
                    `/api/divesites/${initialDiveData.divesite}`,
                    {
                        headers: {
                            Authorization: `Bearer ${firebaseToken}`,
                        },
                    }
                );
                initialDiveData = {
                    ...initialDiveData,
                    divesiteDetails: divesiteResponse.data.uddf.divesite.site,
                }
            }
            catch (divesiteError) {
              console.error("Error fetching details:", divesiteError);
            }
            // Set the dive data from the response for the form
            setDiveData(initialDiveData);
          
        } else {
          setError("Token validation failed");
        }
      } catch (error) {
        console.error("Error validating token:", error);
        setError(error.response?.data?.message || "Failed to validate token");
      } finally {
        setLoading(false);
      }
    };

    // Clear any previous pending token in session storage
    const pendingToken = sessionStorage.getItem("pendingClaimToken");
    if (pendingToken === token) {
      sessionStorage.removeItem("pendingClaimToken");
    }

    validateToken();
  }, [token, currentUser, navigate]);

  // When the dive is successfully submitted
  const handleDiveSubmitted = (submittedDive) => {
    setClaimCompleted(true);
    setDiveData(submittedDive);
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", my: 4 }}>
      <Paper elevation={2} sx={{ overflow: "hidden" }}>
        {isMobile ? (
          // Mobile layout - vertical
          <Box>
            <Box sx={{ p: 2 }}>
              <QRCodeSection token={token} />
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              {currentUser ? (
                <DiveFormSection
                  loading={loading}
                  error={error}
                  validToken={validToken}
                  diveData={diveData}
                  claimCompleted={claimCompleted}
                  onDiveSubmitted={handleDiveSubmitted}
                />
              ) : (
                <LoginSection token={token} />
              )}
            </Box>
          </Box>
        ) : (
          // Desktop layout - horizontal
          <Box sx={{ display: "flex", minHeight: "70vh" }}>
            <Box
              sx={{
                flex: 1,
                borderRight: `1px solid ${theme.palette.divider}`,
              }}
            >
              <QRCodeSection token={token} />
            </Box>
            <Box sx={{ flex: 2 }}>
              {currentUser ? (
                <DiveFormSection
                  loading={loading}
                  error={error}
                  validToken={validToken}
                  diveData={diveData}
                  claimCompleted={claimCompleted}
                  onDiveSubmitted={handleDiveSubmitted}
                />
              ) : (
                <LoginSection token={token} />
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ClaimDivePage;
